import { type AnimationClip } from 'three'

export interface ClipUndoState {
  display_clip: AnimationClip
  original_clip: AnimationClip
}

/**
 * Per-clip undo/redo stack for user-authored animation clips. Stores tuples of
 * `(display_clip, original_clip)` so we can flip back and forth between past
 * versions cleanly.
 *
 * Why a custom stack instead of reusing `UndoRedoSystem`:
 * `UndoRedoSystem` is keyed to a single `Skeleton` and stores bone-transform
 * snapshots — the wrong abstraction for clip-level history. KeyframeBuilder is
 * non-mutating by design, so every authoring action produces a NEW AnimationClip;
 * the stack just remembers the previous reference and restores it on undo.
 *
 * Push semantics: `push_state` records the *current* state before the caller
 * mutates the pair. So the typical flow is:
 *   stack.push_state(pair.display_clip, pair.original_clip)  // snapshot pre-mutation
 *   pair.display_clip = KeyframeBuilder.upsert_pose_keyframe(...)
 *   pair.original_clip = ...
 *
 * Then on undo, the popped state IS the pre-mutation state.
 */
export class ClipUndoRedoStack {
  private undo_stack: ClipUndoState[] = []
  private redo_stack: ClipUndoState[] = []
  private readonly max_history_size: number

  constructor (max_history_size: number = 50) {
    this.max_history_size = max_history_size
  }

  /**
   * Snapshot the given state (the *current* state, before the caller mutates the
   * pair). Clears the redo stack — pushing a new state branches the timeline.
   */
  push_state (display_clip: AnimationClip, original_clip: AnimationClip): void {
    this.undo_stack.push({ display_clip, original_clip })
    if (this.undo_stack.length > this.max_history_size) {
      this.undo_stack.shift()
    }
    this.redo_stack = []
  }

  /**
   * Returns the previous state (popping it off the undo stack), and pushes the
   * given current state onto the redo stack. Returns `null` if the undo stack is
   * empty — caller should leave the pair untouched.
   */
  undo (current_display: AnimationClip, current_original: AnimationClip): ClipUndoState | null {
    const previous = this.undo_stack.pop()
    if (previous === undefined) return null
    this.redo_stack.push({ display_clip: current_display, original_clip: current_original })
    return previous
  }

  /**
   * Returns the next state from the redo stack and pushes the current state onto
   * undo. Returns `null` if redo stack is empty.
   */
  redo (current_display: AnimationClip, current_original: AnimationClip): ClipUndoState | null {
    const next = this.redo_stack.pop()
    if (next === undefined) return null
    this.undo_stack.push({ display_clip: current_display, original_clip: current_original })
    return next
  }

  can_undo (): boolean {
    return this.undo_stack.length > 0
  }

  can_redo (): boolean {
    return this.redo_stack.length > 0
  }

  clear (): void {
    this.undo_stack = []
    this.redo_stack = []
  }
}
